'use client';

import { useSaveForLater } from '@contexts/ProductListsContext';
import { analytics } from '@lib/analytics/page-events';
import { buildCheckoutUrl } from '@lib/commerce/buildCheckoutUrl';
import {
  followCartOrganization,
  forgetCartOrganization,
  isCartOrganizationStale,
  notifyCartOrganizationChanged,
  subscribeToCartOrganizationStale,
  subscribeToCartRepriced,
} from '@lib/commerce/cartChanged';
import {
  buildCartCountPath,
  buildGuestCartPath,
  markCheckoutDeparture,
} from '@lib/commerce/cartCount';
import {
  countryCodeFromLocale,
  resolveCommerceEnv,
} from '@lib/commerce/commerceEnvironment';
import { shareCommerceReferenceQueries } from '@lib/commerce/commerceReferenceQueries';
import { readGuestCartId, subscribeToGuestCart } from '@lib/commerce/guestCart';
import { invalidateMiniCartCache } from '@lib/commerce/miniCartCache';
import { useBridgeTokenProvisioning } from '@lib/commerce/useBridgeToken';
import { useCartCurrency } from '@lib/commerce/useCartCurrency';
import { useCartUserId } from '@lib/commerce/useCartUserId';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { useIsClient } from '@lib/hooks/useIsClient';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import {
  readTabActiveOrgCookie,
  subscribeToActiveOrgCookie,
} from '@lib/util/activeOrgCookie';
import { resolveActiveOrg } from '@lib/util/resolveActiveOrg';
import * as Sentry from '@sentry/nextjs';
import type { QueryClient } from '@tanstack/react-query';
import { getGetCartQueryKey, type LineItem } from '@unity/commerce-bridge';
import type { CartManagementInitialValues } from '@unity/commerce-core';
import { useSession } from 'next-auth/react';
import { useParams } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MutableRefObject,
} from 'react';
import useSWR from 'swr';
import { resolveAssetStoreProductId } from './assetStoreProductId';
import { getMiniCartClient, miniCartNavigation } from './miniCartClient';
import { createMiniCartPersister } from './miniCartPersister';
import {
  CART_QUERY_KEY,
  createMiniCartQueryClient,
  isLineInCart,
} from './miniCartQueryClient';
import { createProductProjectionFetcher } from './productProjections';
import { retryLineWritesOnConflict } from './retryLineWritesOnConflict';
import {
  createSaveForLaterAdapter,
  type MiniCartSnapshot,
} from './saveForLaterAdapter';
import { useMiniCartCache } from './useMiniCartCache';

const STORE = 'asset';
const DEFAULT_LOCALE = 'en-US';

export type MiniCartIslandConfig = {
  /** Session and flags are known, so the seeded initialValues are final. */
  isReady: boolean;
  /** A guest whose cart commerce-core holds. */
  hasGuestCart: boolean;
  /** Remounts the provider when the shopper signs in or out, as it seeds once. */
  providerKey: string;
  isGuest: boolean;
  /** The guest cart this browser holds; the provider follows it without a remount. */
  guestCartId?: string;
  /** A guest known to have no cart yet, so their first add changes the count. */
  hasNoGuestCart: boolean;
  /** commerce-core's refetch ignores `enabled`: never reread without this. */
  canReadCart: boolean;
  /**
   * Another tab moved the cart, one per user, to another org: reading it
   * here would move it back, so the island neither reads nor writes it.
   */
  hasCartOrganizationChanged: boolean;
  hasProvisioningError: boolean;
  onRetryProvisioning: () => void;
  hasOrganizationError: boolean;
  onRetryOrganization: () => void;
  /** Either failure above: no cart can be read until a retry succeeds. */
  hasLoadError: boolean;
  /** Retries whichever of the two failed. */
  onRetryLoad: () => void;
  locale: string;
  currency: string;
  /** Withheld until organizationId is known (see useCartUserId). */
  userId?: string;
  /** Withheld until the bridge token exists (see CommerceCartProvider). */
  organizationId?: string;
  initialValues: CartManagementInitialValues;
  /** For the island's commerce-core provider; see createMiniCartQueryClient. */
  queryClient: QueryClient;
  /** Written by the provider subtree; read by save-for-later at call time. */
  snapshotRef: MutableRefObject<MiniCartSnapshot>;
  /** Gets commerce-ui's checkout cart URL, which only a guest is sent to. */
  onViewCart: (url: string) => void;
  onLineItemsRemoved: (lineItems: LineItem[]) => void;
  /**
   * Bumped by every failed save for later; `undefined` once dismissed or
   * after a save or line write that lands.
   */
  saveForLaterErrorId?: number;
  onDismissSaveForLaterError: () => void;
  /**
   * Bumped by every failed removal or seat change, once the lines are
   * resynced; `undefined` once dismissed or after a line write that lands.
   */
  updateErrorId?: number;
  onDismissUpdateError: () => void;
};

/**
 * Headless configuration for the mini-cart island (CPS-7696): who the
 * shopper is, which org's cart to read, the commerce-core provider values,
 * and the handlers the island wires into commerce-ui. Must render inside
 * ProductListsProvider, which `useSaveForLater` reads.
 */
export const useMiniCartIslandConfig = (): MiniCartIslandConfig => {
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || DEFAULT_LOCALE;
  const currency = useCartCurrency();
  const { flags, loading: isFlagsLoading } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { data: session, status } = useSession();
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;
  const {
    isReady: isBridgeTokenReady,
    isError: hasProvisioningError,
    retry: onRetryProvisioning,
  } = useBridgeTokenProvisioning();
  // A guest reads the cart commerce-core keeps for them (CPS-7693); without
  // one the mini-cart reads nothing.
  const isGuest = status === 'unauthenticated';
  const guestCartId = useSyncExternalStore(
    subscribeToGuestCart,
    readGuestCartId,
    () => undefined
  );
  const hasGuestCart = isGuest && Boolean(guestCartId);
  // The provider seeds once, at the first render, which is the page's
  // hydration: the store snapshot above is still the server's there. A
  // remount, on signing in or out, seeds again from the cart held by then.
  const providerKey = isGuest ? 'guest' : 'user';
  const [seeded, setSeeded] = useState(() => ({
    key: providerKey,
    guestCartId: readGuestCartId(),
  }));
  let seededGuestCartId = seeded.guestCartId;
  if (seeded.key !== providerKey) {
    seededGuestCartId = guestCartId;
    setSeeded({ key: providerKey, guestCartId });
  }
  const isClient = useIsClient();
  const { onSaveForLater } = useSaveForLater();

  // Same key as OrganizationsContext, so one request serves both.
  const {
    data: orgsData,
    error: organizationError,
    mutate: retryOrganization,
  } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId?: string;
  }>(userId ? ['/api/users/organizations', userId] : null, ([url]) =>
    failingFetcher(url)
  );
  const onRetryOrganization = useCallback(() => {
    // SWR retains the error if this retry also fails.
    void retryOrganization(undefined, {
      revalidate: true,
      throwOnError: false,
    });
  }, [retryOrganization]);
  // The org this tab works in, as the header count reads it and a PDP was
  // rendered for: a mini-cart following another tab's switch would move the
  // cart to an org the page on screen was not rendered for.
  const activeOrgCookie = useSyncExternalStore(
    subscribeToActiveOrgCookie,
    readTabActiveOrgCookie,
    () => undefined
  );
  const activeOrganizationId = resolveActiveOrg(
    activeOrgCookie,
    orgsData?.organizations,
    orgsData?.defaultOrganizationId
  )?.id;
  const organizationId = isBridgeTokenReady ? activeOrganizationId : undefined;
  const cartUserId = useCartUserId(userId, organizationId);
  // Before the provider below reads for it; a switch here is the shopper's.
  useLayoutEffect(() => {
    if (organizationId) followCartOrganization(organizationId);
  }, [organizationId]);
  // Signed out, here or in another tab (which leaves this one where it is):
  // a guest cart is not the org cart another tab moved.
  useLayoutEffect(() => {
    if (isGuest) forgetCartOrganization();
  }, [isGuest]);
  const isOrganizationStale = useSyncExternalStore(
    subscribeToCartOrganizationStale,
    isCartOrganizationStale,
    () => false
  );
  const hasCartOrganizationChanged = Boolean(userId) && isOrganizationStale;

  const snapshotRef = useRef<MiniCartSnapshot>({
    products: undefined,
    lineItems: [],
  });
  // Line items this tab asked to remove: a refetch also drops lines removed
  // in another tab, which are not this shopper's action here.
  const removingLineIdsRef = useRef(new Set<string>());
  // commerce-core removes a saved line item itself; that removal is the save,
  // not a separate remove action.
  const savedLineIdsRef = useRef(new Set<string>());
  // Removed for a late save: the shopper may have removed them meanwhile.
  const lateSavedLineIdsRef = useRef(new Set<string>());
  const [persister] = useState(createMiniCartPersister);
  const [updateErrorId, setUpdateErrorId] = useState<number>();
  const onDismissUpdateError = useCallback(
    () => setUpdateErrorId(undefined),
    []
  );
  const [saveForLaterErrorId, setSaveForLaterErrorId] = useState<number>();
  const onDismissSaveForLaterError = useCallback(
    () => setSaveForLaterErrorId(undefined),
    []
  );
  const [queryClient] = useState(() =>
    createMiniCartQueryClient({
      onRemoveStarted: (lineIds) =>
        lineIds.forEach((id) => removingLineIdsRef.current.add(id)),
      onRemoveFailed: (lineIds) =>
        lineIds.forEach((id) => {
          removingLineIdsRef.current.delete(id);
          savedLineIdsRef.current.delete(id);
        }),
      // Released as the removal starts, so no marker outlives its line.
      takeMayFindLinesGone: (lineIds) => {
        const lateSaved = lateSavedLineIdsRef.current;
        const mayBeGone = lineIds.every((id) => lateSaved.has(id));
        lineIds.forEach((id) => lateSaved.delete(id));
        return mayBeGone;
      },
      variantKeyOf: (lineItemId) =>
        snapshotRef.current.lineItems.find(({ id }) => id === lineItemId)
          ?.variant?.key,
      onWriteSettled: persister.invalidate,
      onLineWriteFailed: () => setUpdateErrorId((id = 0) => id + 1),
      // The cart wrote, so neither error describes it now.
      onLineWriteSucceeded: () => {
        setUpdateErrorId(undefined);
        setSaveForLaterErrorId(undefined);
      },
    })
  );
  useMiniCartCache({
    persister,
    queryClient,
    sessionStatus: status,
    userId,
    organizationId,
    currency,
  });
  // What the cart query holds is the old org's cart, at a version since
  // moved on: a provider mounted again for a switch here must not show it.
  useEffect(() => {
    if (hasCartOrganizationChanged) {
      queryClient.removeQueries({ queryKey: CART_QUERY_KEY });
    }
  }, [hasCartOrganizationChanged, queryClient]);
  // The PDP's provider and this one read the same org and user context.
  useEffect(() => shareCommerceReferenceQueries(queryClient), [queryClient]);
  // The PDP's reread after a cart change may re-price the cart, moving its
  // version past the one the mini-cart holds: reread it, or its next
  // write is refused with a 412.
  useEffect(
    () =>
      subscribeToCartRepriced(() => {
        if (isCartOrganizationStale()) return;
        void queryClient.invalidateQueries({
          // commerce-core keys a guest cart by its id.
          queryKey: getGetCartQueryKey(
            cartUserId ? `user:${cartUserId}` : (readGuestCartId() ?? '')
          ),
          exact: true,
        });
      }),
    [queryClient, cartUserId]
  );
  const latestRef = useRef({
    userId,
    organizationId,
    isGuest,
    locale,
    onSaveForLater,
  });
  latestRef.current = {
    userId,
    organizationId,
    isGuest,
    locale,
    onSaveForLater,
  };
  // The read commerce-core makes, by user and org, or a guest's by its id.
  useEffect(
    () =>
      retryLineWritesOnConflict(getMiniCartClient(), (cartId) => {
        const { userId: user, organizationId: org } = latestRef.current;
        if (!user) return buildGuestCartPath(cartId);
        return org
          ? buildCartCountPath({ userId: user, organizationId: org })
          : undefined;
      }),
    []
  );
  // commerce-core seeds onSaveForLater once, so it must never change.
  const [saveForLaterAdapter] = useState(() =>
    createSaveForLaterAdapter({
      getSnapshot: () => snapshotRef.current,
      fetchProduct: createProductProjectionFetcher(getMiniCartClient()),
      saveForLater: (params) => latestRef.current.onSaveForLater(params),
      // The cart changes, and is reread, once the removal that follows lands.
      onSaved: ({ productId, assetStoreProductId, isLate }) => {
        const { lineItems, removeLineItem } = snapshotRef.current;
        for (const lineItem of lineItems) {
          const { id } = lineItem;
          if (!id || lineItem.productId !== productId) continue;
          // The shopper may have removed it while the save was pending.
          if (isLate && !isLineInCart(queryClient, id)) continue;
          savedLineIdsRef.current.add(id);
          // commerce-core kept the line of a save it was told failed (S2-11).
          if (isLate) {
            lateSavedLineIdsRef.current.add(id);
            removeLineItem?.({ lineItemId: id });
          }
        }
        setSaveForLaterErrorId(undefined);
        analytics.pushMiniCartActionEvent({
          mini_cart_action: 'save_for_later',
          asset_id: assetStoreProductId,
        });
      },
      // commerce-core only logs a failure; the line stays in the cart.
      onFailed: () => setSaveForLaterErrorId((id = 0) => id + 1),
    })
  );

  const initialValues = useMemo<CartManagementInitialValues>(
    () => ({
      store: STORE,
      client: getMiniCartClient(),
      currencyCode: currency,
      countryCode: countryCodeFromLocale(locale),
      env: resolveCommerceEnv(),
      // The badge reads the cart on every page, but never to
      // create one, and none of the checkout-grade address/tax/proration
      // checks. A guest without a cart has nothing to read: commerce-core
      // would create one.
      enableCartFetching: !isGuest || Boolean(seededGuestCartId),
      ensureCreateAuthenticatedCart: false,
      // No setter exists, so on from the start for a guest, whose first cart
      // is created elsewhere. Also for a signed-in user still holding a guest
      // cart: commerce-core merges it into their cart once their org is known.
      enableAnonymousCart: isGuest || Boolean(seededGuestCartId),
      enableCartChecks: false,
      // Re-price in the footer currency, as the PDP does.
      syncCartCurrency: true,
      // commerce-ui shows the save button only when this is set. Lists
      // belong to a signed-in user, so a guest is not offered it.
      onSaveForLater:
        isProductListsEnabled && !isGuest ? saveForLaterAdapter : undefined,
      // Reading the cart for an org moves it there: other tabs are told.
      onOrganizationChange: notifyCartOrganizationChanged,
    }),
    [
      currency,
      locale,
      isGuest,
      seededGuestCartId,
      isProductListsEnabled,
      saveForLaterAdapter,
    ]
  );

  // commerce-ui's URL carries a guest's cart by its id; a signed-in user's
  // goes to the active org's cart in this locale, as PDP Buy Now does.
  const onViewCart = useCallback((guestCartUrl: string) => {
    const {
      isGuest: isGuestCart,
      organizationId: orgId,
      locale: viewLocale,
    } = latestRef.current;
    analytics.pushMiniCartActionEvent({
      mini_cart_action: 'view_cart',
      item_count: snapshotRef.current.lineItems.length,
    });
    if (isGuestCart) {
      markCheckoutDeparture();
      miniCartNavigation.assign(guestCartUrl);
      return;
    }
    // Checkout resolves the cart from organizationId; without it the
    // redirect lands on an org-less cart checkout cannot identify.
    if (!orgId) {
      Sentry.captureException(
        new Error('organizationId missing on mini-cart View cart')
      );
      return;
    }
    // Checkout writes the cart where no signal of ours reaches.
    invalidateMiniCartCache();
    markCheckoutDeparture();
    miniCartNavigation.assign(
      buildCheckoutUrl({
        env: resolveCommerceEnv(),
        locale: viewLocale,
        organizationId: orgId,
        store: STORE,
      })
    );
  }, []);

  // The removal's own settling already told the header and the PDP.
  const onLineItemsRemoved = useCallback((lineItems: LineItem[]) => {
    for (const lineItem of lineItems) {
      const lineId = lineItem.id ?? '';
      if (
        !removingLineIdsRef.current.delete(lineId) ||
        savedLineIdsRef.current.delete(lineId)
      ) {
        continue;
      }
      analytics.pushMiniCartActionEvent({
        mini_cart_action: 'remove_item',
        asset_id: resolveAssetStoreProductId(
          snapshotRef.current.products?.find(
            ({ id }) => id === lineItem.productId
          ),
          lineItem.productKey
        ),
      });
    }
  }, []);

  const hasOrganizationLoadError = Boolean(organizationError) && !orgsData;
  const onRetryLoad = useCallback(() => {
    if (hasProvisioningError) onRetryProvisioning();
    if (hasOrganizationLoadError) onRetryOrganization();
  }, [
    hasProvisioningError,
    hasOrganizationLoadError,
    onRetryProvisioning,
    onRetryOrganization,
  ]);

  return {
    isReady: !isFlagsLoading && status !== 'loading' && isClient,
    providerKey,
    isGuest,
    guestCartId,
    hasGuestCart,
    hasNoGuestCart: isGuest && !guestCartId,
    canReadCart:
      (userId ? Boolean(organizationId) : hasGuestCart) &&
      !hasCartOrganizationChanged,
    hasCartOrganizationChanged,
    hasProvisioningError,
    onRetryProvisioning,
    hasOrganizationError: hasOrganizationLoadError,
    onRetryOrganization,
    hasLoadError: hasProvisioningError || hasOrganizationLoadError,
    onRetryLoad,
    locale,
    currency,
    userId: cartUserId,
    organizationId,
    initialValues,
    queryClient,
    snapshotRef,
    onViewCart,
    onLineItemsRemoved,
    saveForLaterErrorId,
    onDismissSaveForLaterError,
    updateErrorId,
    onDismissUpdateError,
  };
};
